import Enum from '@/modules/core/app/helpers/Enum';
import { Estate } from '@/modules/estate/models/Estate';
import { EstateTaskDocumentStatus } from '@/modules/estatetask/enums/EstateTaskDocumentStatus';
import { EstateTaskEditorTemplateDocumentStatus } from '@/modules/estatetask/enums/EstateTaskEditorTemplateDocumentStatus';
import { EstateTaskEditorTemplateDocumentTypeKey } from '@/modules/estatetask/enums/EstateTaskEditorTemplateDocumentTypeKey';
import { EstateTaskUserTypeKey } from '@/modules/estatetask/enums/EstateTaskUserTypeKey';
import { LastApprovedOrRejectedByUser } from '@/modules/estatetask/models/LastApprovedOrRejectedByUser';
import { EstateTaxForm } from '@/modules/estatetaxform/models/EstateTaxForm';
import { EditorTemplateDocument } from '@/modules/export/_modules/editor/models/EditorTemplateDocument';
import { Profession } from '@/modules/professional/models/Profession';
import { ProtectionMandate } from '@/modules/protectionmandate/models/ProtectionMandate';
import { Team } from '@/modules/team/models/Team';
import { TimeEntry } from '@/modules/timeentry/models/TimeEntry';
import { User } from '@/modules/user/models/User';
import { Will } from '@/modules/will/models/Will';
import { Workflow } from '@/modules/workflow/models/Workflow';
import dayjs from 'dayjs';
import { DateFormat } from '/@/modules/core/app/constants/date.constants';
import { ModelType } from '/@/modules/core/app/constants/ModelType';
import {
  endDay,
  isInNextWeek,
  isInThisWeek,
  isToday,
  isTodayOrBefore,
  isYesterday,
  numberOfDaysFromTodayToDate
} from '/@/modules/core/app/helpers/DateHelper';
import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import Lang from '/@/modules/core/app/services/Lang';
import EstateTaskStatusFactory from '/@/modules/estatetask/services/EstateTaskStatusFactory';

export class EstateTask extends BaseEntityModel {
  id?: number;
  estateId?: number;
  modelType?: string;
  modelId?: number;
  title?: string;
  description?: string;
  userId?: number;
  teamId?: number;
  status?: string;
  priority?: string;
  complexity?: string;
  type?: EstateTaskUserTypeKey | EstateTaskEditorTemplateDocumentTypeKey | null;
  createdByType?: ModelType;
  createdById?: number;
  startDate?: string;
  dueDate?: string;
  closedDate?: string;
  doneDate?: string;
  deadlineDate?: string;
  estate?: Relationship<Estate | null>;
  user?: Relationship<User | null>;
  profession?: Relationship<Profession | null>;
  will?: Relationship<Will | null>;
  protectionMandate?: Relationship<ProtectionMandate | null>;
  timeEntries?: Relationship<TimeEntry[] | null>;
  team?: Relationship<Team | null>;
  createdByUser?: Relationship<User | null>;
  createdByWorkflow?: Relationship<Workflow | null>;
  estateTaxForm?: Relationship<EstateTaxForm | null>;
  editorTemplateDocument?: Relationship<EditorTemplateDocument | null>;
  lastApprovedOrRejectedByUser?: LastApprovedOrRejectedByUser | null;
  reviewedAtLeastOnce?: boolean;

  constructor(model: Partial<EstateTask> = {}) {
    super(model);

    this.id = model.id;
    this.estateId = model.estateId;
    this.modelType = model.modelType;
    this.modelId = model.modelId;
    this.title = model.title;
    this.description = model.description ?? '';
    this.userId = model.userId;
    this.teamId = model.teamId;
    this.status = model.status;
    this.priority = model.priority;
    this.complexity = model.complexity;
    this.type = model.type;
    this.createdByType = model.createdByType;
    this.createdById = model.createdById;
    this.startDate = model.startDate;
    this.dueDate = model.dueDate;
    this.closedDate = model.closedDate;
    this.doneDate = model.doneDate;
    this.deadlineDate = model.deadlineDate;
    this.reviewedAtLeastOnce = model.reviewedAtLeastOnce;

    this.estate = Estate.mapModelOrNull(model.estate);
    this.user = User.mapModelOrNull(model.user);
    this.profession = Profession.mapModelOrNull(model.profession);
    this.will = Will.mapModelOrNull(model.will);
    this.protectionMandate = ProtectionMandate.mapModelOrNull(model.protectionMandate);
    this.team = Team.mapModelOrNull(model.team);
    this.timeEntries = TimeEntry.mapModelsOrNull(model.timeEntries);
    this.createdByUser = User.mapModelOrNull(model.createdByUser);
    this.createdByWorkflow = Workflow.mapModelOrNull(model.createdByWorkflow);
    this.estateTaxForm = EstateTaxForm.mapModelOrNull(model.estateTaxForm);
    this.editorTemplateDocument = EditorTemplateDocument.mapModelOrNull(
      model.editorTemplateDocument
    );
    this.lastApprovedOrRejectedByUser = LastApprovedOrRejectedByUser.mapModelOrNull(
      model.lastApprovedOrRejectedByUser
    );
  }

  getModelName(): ModelType {
    return ModelType.ESTATE_TASK;
  }

  get estateTaskStatus(): Enum {
    return EstateTaskStatusFactory.build(this.modelType);
  }

  get isDueToday(): boolean {
    return isToday(this.dueDate);
  }

  get isDueThisWeek(): boolean {
    return isInThisWeek(this.dueDate);
  }

  get isDueNextWeek(): boolean {
    return isInNextWeek(this.dueDate);
  }

  get displayDueDate(): string {
    if (!this.dueDate) {
      return '-';
    }

    if (this.isDueToday) {
      return Lang.t('estatetask.due-today');
    }

    if (isYesterday(this.dueDate)) {
      return Lang.t('estatetask.due-yesterday');
    }

    const days: number = numberOfDaysFromTodayToDate(this.dueDate);
    if (days <= 2 && days >= -2) {
      return Lang.t('estatetask.due-ago', {
        daysAgo: dayjs(endDay(this.dueDate)).from(endDay(dayjs().toISOString()))
      });
    }

    return dayjs(this.dueDate).format(DateFormat.MONTH_DAY_YEAR);
  }

  get isStatusSentForReview(): boolean {
    return this.status === EstateTaskDocumentStatus.SENT_FOR_REVIEW.key;
  }

  get isStatusApproved(): boolean {
    return (
      this.status === EstateTaskEditorTemplateDocumentStatus.APPROVED ||
      this.status === EstateTaskEditorTemplateDocumentStatus.APPROVED_AUTOMATED
    );
  }

  /**
   * Due in a day or today
   */
  get isDueSoon(): boolean {
    if (!this.dueDate) {
      return false;
    }

    return isTodayOrBefore(this.dueDate);
  }

  get isPastDueDate(): boolean {
    if (!this.dueDate) {
      return false;
    }

    return isTodayOrBefore(this.dueDate);
  }

  isByUser(userId: number): boolean {
    return this.userId === userId;
  }
}
